import React, { InputHTMLAttributes } from "react";
import {
  Control,
  Controller,
  FieldValues,
  Path,
  RegisterOptions,
} from "react-hook-form";
import { PatternFormat } from "react-number-format";
import { isValidNumberForRegion } from "libphonenumber-js";

interface PhoneNumberFieldProps<FieldsType extends FieldValues> {
  name: Path<FieldsType>;
  control: Control<FieldsType>;
  rules?: RegisterOptions<FieldsType>;
  inputProps?: InputHTMLAttributes<HTMLInputElement>;
  label?: string;
  required?: boolean;
  shouldUnregister?: boolean;
  requiredMessage?: string;
  className?: string;
  wrapperClassName?: string;
  labelClassName?: string;
  showError?: boolean;
}

export default function PhoneNumberField<FieldsType extends FieldValues>({
  name,
  label,
  control,
  required,
  shouldUnregister,
  requiredMessage,
  rules = {},
  inputProps = {},
  className,
  wrapperClassName = "",
  labelClassName = "",
  showError = true,
}: PhoneNumberFieldProps<FieldsType>) {
  return (
    <Controller
      name={name}
      control={control}
      shouldUnregister={shouldUnregister}
      rules={{
        required: requiredMessage || (required && "Required"),
        validate: (val) => {
          const value = val || "";
          const isNumberValid = isValidNumberForRegion(value, "US");
          if (!isNumberValid && value.length > 0)
            return "Mobile Number is Not valid";
        },
        ...rules,
      }}
      render={({
        field: { value, ref, name, onChange },
        fieldState: { error },
      }) => (
        <div className={`relative ${wrapperClassName}`}>
          {label && (
            <label
              className={`inline-block text-gray-500 pb-1 text-sm ${labelClassName}`}
            >
              {label}
              {(required || requiredMessage) && (
                <span className="text-red-600 ml-0.5 text-base">*</span>
              )}
            </label>
          )}
          <PatternFormat
            className={`w-full appearance-none border border-gray-300 bg-gray-50  px-3 py-2.5 rounded-lg disabled:bg-gray-50 disabled:border-gray-200 text-gray-900 placeholder-gray-500 focus:outline-none focus:shadow-none focus:ring-0 focus:border-gray-900 text-sm ${className} ${
              !showError && error?.message
                ? "border-red-500 focus:border-red-500"
                : ""
            }`}
            format="(###) ### ####"
            mask="_"
            getInputRef={ref}
            placeholder="(987) 456 3210"
            onChange={onChange}
            {...inputProps}
            defaultValue={value}
            value={value}
            type="text"
          />
          {showError ? (
            <p className="text-red-600 font-normal text-sm m-0 p-0 ml-1  bottom-0 left-0 right-0">
              {error?.message}
            </p>
          ) : null}
        </div>
      )}
    />
  );
}
